// @flow
import _ from 'lodash'
import * as React from 'react'
import type { Answer } from 'types/answer'
import type { Page } from 'types/page'
import type { Question } from 'types/question'
import type { Response } from 'types/response'
import formAnswersHelpers from 'utils/formAnswerHelpers'
import { visibleByConditionalRulesHelper } from 'utils/formHelpers'

import FormPage from './FormPage'
import ZentakeFormContext from './ZentakeFormContext'

export type Props = {
  answers: Answer[],
  client: Client,
  formName: string,
  getAnswer: (question: Question) => Answer,
  getAnswers: (question: Question) => Answer[],
  getAnswerCell: (question: Question) => Answer,
  getAnswerSignature: (question: Question) => Answer,
  getQuestionError: (question: Question) => string | null,
  getTableQuestionExtraRows: (question: Question) => Answer[],
  isInternalForm: boolean,
  isQuestionDirty: (question: Question) => boolean,
  isQuestionReadonly: (question: Question) => boolean,
  isQuestionValid: (question: Question) => boolean,
  mode: 'preview' | 'edit' | 'client' | 'office',
  onAnswerCellChanged: (question: Question, answer: Answer) => void,
  onAnswerChanged: (question: Question, answer: Answer) => void,
  onAnswerSignatureChanged: (question: Question, answer: Answer) => void,
  questionIdToScroll: string,
  responseView: string,
  setQuestionIdToScroll: (questionId: string) => void,
  setResponse: (response: Response) => void,
  setStripeEmpty: (stripeEmpty: boolean) => void,
  setStripeError: (stripeError: string) => void,
  signatureMemo: string,
  stripeEmpty: boolean,
  stripeError: string,
  templateId: string,
  visibleByConditionalRules: (question: Question) => boolean,
  willShowNotAllowedCursor: (question: Question) => boolean,
  repeatSectionInfo?: {
    pageId: string,
    originalPageId: string,
    order: number,
  } | null,
}

function ZentakeFormProvider({
  answers,
  client,
  form,
  getQuestionError,
  hideName,
  isInternalForm,
  isQuestionDirty,
  isQuestionValid,
  mode,
  onAnswerCellChanged,
  onAnswerChanged,
  onAnswerSignatureChanged,
  questionIdToScroll,
  questions,
  responseView,
  setQuestionIdToScroll,
  setResponse,
  setStripeEmpty,
  setStripeError,
  signatureMemo,
  stripeEmpty,
  stripeError,
  templateId,
  response,
  sessions,
}: Props): React.Node {
  const shouldRenderPage = React.useCallback(
    (page: Page, allPages: Page[] = []) => {
      const questions = []

      const collectQuestionsFromPage = (currentPage: Page) => {
        currentPage.sections.forEach((section) => {
          if (section.rows) {
            section.rows.forEach((row) => {
              row.questions.forEach((question) => {
                questions.push(question)
              })
            })
          }

          // Fix for old serializer in snapshot
          if (section.questions) {
            // $FlowFixMe
            section.questions.forEach((question) => {
              questions.push(question)
            })
          }
        })

        // Collect questions from child pages
        const childPages = allPages.filter(
          (p) => p.parent_page_id === currentPage.id
        )
        childPages.forEach((childPage) => {
          collectQuestionsFromPage(childPage)
        })
      }

      collectQuestionsFromPage(page)

      return questions.some((question) =>
        visibleByConditionalRulesHelper(question, answers, questions)
      )
    },
    [answers]
  )

  const renderPages = React.useCallback(() => {
    if (!form?.pages) return null
    const mainPages = form.pages.filter((page) => !page.parent_page_id)
    return _.orderBy(mainPages, ['order'], ['asc']).map((p) => {
      return (
        <FormPage
          allPages={form.pages}
          answers={answers}
          hidden={!shouldRenderPage(p, form.pages)}
          key={p.id}
          page={p}
          response={response}
          sessions={sessions}
        />
      )
    })
  }, [shouldRenderPage, response, sessions, answers, form?.pages])

  const ZentakeForm = React.useCallback(
    ({ _hideName, _formName }) => {
      return (
        <>
          {!_hideName && (
            <div className="ml-4">
              <h3 className="mb-6 inline-block text-center text-2xl font-medium leading-6 text-gray-900">
                {_formName}
              </h3>
            </div>
          )}
          <div className="flex-1">{renderPages()}</div>
        </>
      )
    },
    [renderPages]
  )

  const isQuestionReadonly = React.useCallback(
    (question) => {
      if (!question) {
        return false // Should never happen
      }

      return (
        mode === 'read-only' ||
        question?.is_read_only === true ||
        (mode === 'client' && question.is_office_use_only) ||
        (mode === 'office' && !question.is_office_use_only)
      )
    },
    [mode]
  )

  const willShowNotAllowedCursor = React.useCallback(
    (question) => {
      return (
        question?.is_read_only === true ||
        (mode === 'client' && question?.is_office_use_only) ||
        (mode === 'office' && !question?.is_office_use_only)
      )
    },
    [mode]
  )

  const {
    getAnswer,
    getAnswers,
    getAnswerSignature,
    getAnswerCell,
    getTableQuestionExtraRows,
  } = formAnswersHelpers(answers)

  const values = React.useMemo(() => {
    function visibleByConditionalRules(question: Question) {
      return visibleByConditionalRulesHelper(question, answers, questions || [])
    }

    return {
      answers,
      client,
      formName: form?.name,
      getAnswer,
      getAnswers,
      getAnswerCell,
      getAnswerSignature,
      getQuestionError: getQuestionError || (() => null),
      getTableQuestionExtraRows,
      isInternalForm,
      isQuestionDirty: isQuestionDirty || (() => false),
      isQuestionReadonly,
      isQuestionValid: isQuestionValid || (() => true),
      mode,
      onAnswerCellChanged,
      onAnswerChanged,
      onAnswerSignatureChanged,
      questionIdToScroll,
      responseView,
      setQuestionIdToScroll,
      setResponse,
      setStripeEmpty,
      setStripeError,
      signatureMemo,
      stripeEmpty,
      stripeError,
      templateId,
      visibleByConditionalRules,
      willShowNotAllowedCursor,
      repeatSectionInfo: null,
    }
  }, [
    answers,
    client,
    form?.name,
    getAnswer,
    getAnswers,
    getAnswerCell,
    getAnswerSignature,
    getQuestionError,
    getTableQuestionExtraRows,
    isInternalForm,
    isQuestionDirty,
    isQuestionReadonly,
    isQuestionValid,
    mode,
    onAnswerCellChanged,
    onAnswerChanged,
    onAnswerSignatureChanged,
    questionIdToScroll,
    questions,
    responseView,
    setQuestionIdToScroll,
    setResponse,
    setStripeEmpty,
    setStripeError,
    signatureMemo,
    stripeEmpty,
    stripeError,
    templateId,
    willShowNotAllowedCursor,
  ])

  if (!form) return null
  return (
    <ZentakeFormContext.Provider value={values}>
      {ZentakeForm({
        _hideName: hideName,
        _formName: form.name,
      })}
    </ZentakeFormContext.Provider>
  )
}

export default ZentakeFormProvider
